import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState(null);
  const [form, setForm] = useState({
    institution_name: "", institution_type: "University", name: "", email: "",
    phone: "", website: "", country_id: "", city_id: "", password: "",
  });
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/public/filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cities = filters?.cities?.filter((c) => c.country_id === form.country_id) || [];

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agree) { setError("Please accept the Terms & Conditions and Listing Policy."); return; }
    setLoading(true);
    try {
      await register(form);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-slate-50 px-4 py-12">
      <div className="mx-auto w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 text-center">
          <img src="/oed-logo.gif" alt="Online Education Directory" className="mx-auto h-8 w-auto" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-slate-900">Register Your Institution</h1>
          <p className="mt-1 text-sm text-slate-500">List your online programmes on the directory</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="register-error">{error}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Institution Name" required>
              <Input value={form.institution_name} onChange={(e) => set("institution_name", e.target.value)} required data-testid="reg-inst-name" />
            </Field>
            <Field label="Institution Type">
              <Select value={form.institution_type} onValueChange={(v) => set("institution_type", v)}>
                <SelectTrigger data-testid="reg-inst-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["University", "College", "Institute", "Business School"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Country">
              <Select value={form.country_id} onValueChange={(v) => { set("country_id", v); set("city_id", ""); }}>
                <SelectTrigger data-testid="reg-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {(filters?.countries || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="City">
              <Select value={form.city_id} onValueChange={(v) => set("city_id", v)} disabled={!form.country_id}>
                <SelectTrigger data-testid="reg-city"><SelectValue placeholder="Select city" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {cities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Website">
              <Input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" data-testid="reg-website" />
            </Field>
            <Field label="Phone">
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="reg-phone" />
            </Field>
            <Field label="Contact Person" required>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="reg-contact" />
            </Field>
            <Field label="Email" required>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required data-testid="reg-email" />
            </Field>
            <Field label="Password" required>
              <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} data-testid="reg-password" />
            </Field>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-600">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid="reg-agree" className="mt-0.5" />
            <span>I agree to the <Link to="/page/terms" className="text-blue-800 hover:underline">Terms & Conditions</Link> and <Link to="/page/listing-policy" className="text-blue-800 hover:underline">Listing Policy</Link>.</span>
          </label>
          <Button type="submit" className="w-full bg-blue-800 hover:bg-blue-900" disabled={loading} data-testid="register-submit">
            {loading ? "Creating account..." : "Register Institution"}
          </Button>
          <p className="text-center text-sm text-slate-500">
            Already registered? <Link to="/login" className="text-blue-800 hover:underline">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="mb-1.5 block">{label}{required && <span className="text-red-600"> *</span>}</Label>
      {children}
    </div>
  );
}
